import type { BaseInput, Dec } from "@ftr/tax-core";
import { money } from "@ftr/tax-core";
import { Choice, MoneyField, Section, StatusField } from "../components/ui.tsx";
import { collect } from "../lib/fields.ts";
import { specifiedServiceAnswer, type Profile } from "../lib/profile.ts";

export function AboutYou({ profile, update }: { profile: Profile; update: (p: Partial<Profile>) => void }) {
  const joint = profile.status === "mfj";
  return (
    <Section label="About you">
      <StatusField value={profile.status} onChange={(status) => update({ status })} />
      <MoneyField
        label={joint ? "Your W-2 wages for 2026" : "W-2 wages for 2026"}
        optional
        value={profile.wages}
        onChange={(wages) => update({ wages })}
        hint="Box 1, from a regular job, if you have one"
      />
      {joint && (
        <MoneyField
          label="Your spouse's W-2 wages"
          optional
          value={profile.spouseWages}
          onChange={(spouseWages) => update({ spouseWages })}
          hint="Box 1. Your spouse's wages don't count toward your Social Security cap."
        />
      )}
      <details className="more">
        <summary>My W-2 boxes 3 and 5 differ from box 1</summary>
        <p className="field-hint more-intro">Retirement contributions and some benefits make them differ. Left blank, each one is treated as equal to box 1.</p>
        <MoneyField
          label="Your Social Security wages"
          optional
          value={profile.ssWages}
          onChange={(ssWages) => update({ ssWages })}
          hint="Box 3 plus any tips in box 7. Sets how much Social Security tax your trading still owes."
        />
        <MoneyField label="Your Medicare wages" optional value={profile.medicareWages} onChange={(medicareWages) => update({ medicareWages })} hint="Box 5" />
        {joint && (
          <MoneyField
            label="Your spouse's Medicare wages"
            optional
            value={profile.spouseMedicareWages}
            onChange={(spouseMedicareWages) => update({ spouseMedicareWages })}
            hint="Box 5"
          />
        )}
      </details>
      <MoneyField
        label="Interest"
        optional
        value={profile.interest}
        onChange={(interest) => update({ interest })}
        hint="Taxable interest, 1099-INT box 1. It counts toward the 3.8% investment income tax."
      />
      <MoneyField
        label="Other income"
        optional
        value={profile.otherIncome}
        onChange={(otherIncome) => update({ otherIncome })}
        hint="Income without self-employment tax that isn't investment income, such as unemployment"
      />
    </Section>
  );
}

export function QbiQuestion({ profile, update }: { profile: Profile; update: (p: Partial<Profile>) => void }) {
  return (
    <div className="ask">
      <Choice
        legend="One question at your income level: do you treat prop trading as a “specified service”?"
        hint="This decides how much of the 20% business income (QBI) deduction you keep. The IRS lists trading as a specified service, but whether simulated-account payouts count is unsettled, and CPAs disagree. “Not sure” uses the answer with the higher tax."
        value={profile.specifiedService as "yes" | "no" | "unsure" | ""}
        options={[
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
          { value: "unsure", label: "Not sure" },
        ]}
        onChange={(specifiedService) => update({ specifiedService })}
      />
    </div>
  );
}

/** Build the core's base input from the profile plus calculator-specific extras. */
export function baseFrom(
  profile: Profile,
  extras: { otherPropNetProfit?: Dec; net1256?: Dec } = {},
): { base: BaseInput; missing: string[]; errors: string[]; assumedZero: string[]; assumptions: string[] } {
  const joint = profile.status === "mfj";
  const c = collect({
    wages: { raw: profile.wages, label: joint ? "your W-2 wages" : "W-2 wages", optional: true },
    ssWages: { raw: profile.ssWages, label: "Social Security wages", optional: true },
    medicareWages: { raw: profile.medicareWages, label: "Medicare wages", optional: true },
    spouseWages: { raw: joint ? profile.spouseWages : "", label: "spouse's W-2 wages", optional: true },
    spouseMedicareWages: { raw: joint ? profile.spouseMedicareWages : "", label: "spouse's Medicare wages", optional: true },
    interest: { raw: profile.interest, label: "interest", optional: true },
    otherIncome: { raw: profile.otherIncome, label: "other income", optional: true },
  });
  const zero = money("0");
  const v = (k: keyof typeof c.values) => c.values[k] ?? zero;
  const blank = (raw: string) => raw.trim() === "";
  const assumptions: string[] = [];
  // A blank box 3 or box 5 means "same as box 1": say so rather than counting it as $0.
  const ssWages = blank(profile.ssWages) ? v("wages") : v("ssWages");
  const medicareWages = blank(profile.medicareWages) ? v("wages") : v("medicareWages");
  const spouseMedicare = blank(profile.spouseMedicareWages) ? v("spouseWages") : v("spouseMedicareWages");
  if (v("wages").greaterThan(0) && (blank(profile.ssWages) || blank(profile.medicareWages))) {
    assumptions.push("Your W-2 Social Security and Medicare wages (boxes 3 and 5) equal box 1, unless you entered them");
  }
  if (joint) {
    assumptions.push("The trading income is yours, not your spouse's. Your spouse has no self-employment income");
    if (v("spouseWages").greaterThan(0) && blank(profile.spouseMedicareWages)) assumptions.push("Your spouse's Medicare wages (box 5) equal box 1");
  }
  if (profile.status === "mfs") assumptions.push("Your spouse also takes the standard deduction (if they itemize, you must too)");
  const assumedZero = c.assumedZero.filter(
    (l) => !["Social Security wages", "Medicare wages", "spouse's Medicare wages"].includes(l) && (joint || l !== "spouse's W-2 wages"),
  );
  return {
    base: {
      status: profile.status,
      wages: v("wages").plus(joint ? v("spouseWages") : zero),
      traderSocialSecurityWages: ssWages,
      medicareWages: medicareWages.plus(joint ? spouseMedicare : zero),
      withholding: zero,
      interest: v("interest"),
      otherOrdinaryIncome: v("otherIncome"),
      otherPropNetProfit: extras.otherPropNetProfit ?? zero,
      net1256: extras.net1256 ?? zero,
      qbi: { include: true, specifiedService: specifiedServiceAnswer(profile.specifiedService) },
    },
    missing: c.missing,
    errors: c.errors,
    assumedZero,
    assumptions,
  };
}

/** What the prop calculators assume about the business itself. */
export const PROP_BUSINESS_ASSUMPTION =
  "Your prop trading is an active sole-proprietor business, and the expenses you entered can be deducted this year";

/** Lets the user revisit their specified-service answer once they've given one. */
export function QbiAnswer({ profile, update }: { profile: Profile; update: (p: Partial<Profile>) => void }) {
  if (!profile.specifiedService) return null;
  const label = { yes: "Yes", no: "No", unsure: "Not sure" }[profile.specifiedService] ?? profile.specifiedService;
  return (
    <details className="fine">
      <summary>Specified-service answer: {label}</summary>
      <QbiQuestion profile={profile} update={update} />
    </details>
  );
}
